import React, { useState, useCallback, useMemo } from 'react';
import { Trash2, Edit3, Download, Upload, Copy, Search, X } from 'lucide-react';
import { BuildData } from '../../types';
import ConfirmModal from './ConfirmModal';
import { formatDate } from '../../utils/formatDate';
import { isValidBuildData } from '../../utils/buildHelpers';

interface SavedBuildsTabProps {
  builds: BuildData[];
  onLoadBuild: (build: BuildData) => void;
  onDeleteBuild: (id: string) => void;
  onUpdateBuild: (id: string, updates: Partial<BuildData>) => void;
  onExportBuild: (build: BuildData) => void;
  onImportBuild: (buildData: Omit<BuildData, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onDuplicateBuild: (build: BuildData) => void;
  currentBuildId?: string | null;
}

const SavedBuildItem = React.memo(({ 
  build, 
  editingBuildId, 
  editName, 
  setEditName, 
  handleEditSave, 
  handleEditCancel, 
  handleEditStart, 
  onLoadBuild, 
  onExportBuild, 
  handleDeleteClick,
  isCurrent,
  onDuplicateBuild
}: {
  build: BuildData;
  editingBuildId: string | null;
  editName: string;
  setEditName: (name: string) => void;
  handleEditSave: (id: string) => void;
  handleEditCancel: () => void;
  handleEditStart: (build: BuildData) => void;
  onLoadBuild: (build: BuildData) => void;
  onExportBuild: (build: BuildData) => void;
  handleDeleteClick: (build: BuildData) => void;
  isCurrent: boolean;
  onDuplicateBuild: (build: BuildData) => void;
}) => {
  const isEditing = editingBuildId === build.id;

  return (
    <div className={`p-4 rounded-lg border transition-all ${isCurrent ? 'bg-purple-900/30 border-purple-500' : 'bg-slate-800/50 border-slate-700 hover:border-purple-600'}`}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          {isEditing ? (
            <div className="flex items-center gap-2">
              <input
                aria-label="Edit build name"
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="bg-slate-700 text-white px-2 py-1 rounded text-sm"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleEditSave(build.id);
                  if (e.key === 'Escape') handleEditCancel();
                }}
              />
              <button
                onClick={() => handleEditSave(build.id)}
                className="text-green-400 hover:text-green-300"
              >
                ✓
              </button>
              <button
                onClick={handleEditCancel}
                className="text-red-400 hover:text-red-300"
              >
                ✕
              </button>
            </div>
          ) : (
            <h4 className="font-semibold text-white flex items-center gap-2">
              {build.name}
              {isCurrent && <span className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded-full">Current</span>}
            </h4>
          )}
          <div className="text-sm text-slate-400 mt-1">
            <span className="capitalize">{build.knight}</span> •
            <span className="capitalize ml-1">{build.weapon.split('_').join(' ')}</span> •
            <span className="capitalize ml-1">{build.spell.split('_').join(' ')}</span> •
            {build.blessings.length} blessings
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Updated: {formatDate(build.updatedAt)}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onLoadBuild(build)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm transition-colors"
          >
            Load
          </button>
          <button
            onClick={() => onDuplicateBuild(build)}
            className="bg-slate-600 hover:bg-slate-700 text-white p-1 rounded transition-colors"
            title="Duplicate"
          >
            <Copy className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleEditStart(build)}
            className="bg-slate-600 hover:bg-slate-700 text-white p-1 rounded transition-colors"
            title="Rename"
          >
            <Edit3 className="w-4 h-4" />
          </button>
          <button
            onClick={() => onExportBuild(build)}
            className="bg-slate-600 hover:bg-slate-700 text-white p-1 rounded transition-colors"
            title="Export"
          >
            <Download className="w-4 h-4" />
          </button>
          <button
            onClick={() => handleDeleteClick(build)}
            className="bg-red-600 hover:bg-red-700 text-white p-1 rounded transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
});

const SavedBuildsTab: React.FC<SavedBuildsTabProps> = ({
  builds,
  onLoadBuild,
  onDeleteBuild,
  onUpdateBuild,
  onExportBuild,
  onImportBuild,
  currentBuildId,
  onDuplicateBuild
}) => {
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; buildId: string; buildName: string }>({
    isOpen: false,
    buildId: '',
    buildName: ''
  });
  const [editingBuild, setEditingBuild] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const handleDeleteClick = useCallback((build: BuildData) => {
    setDeleteConfirm({
      isOpen: true,
      buildId: build.id,
      buildName: build.name
    });
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    onDeleteBuild(deleteConfirm.buildId);
    setDeleteConfirm({ isOpen: false, buildId: '', buildName: '' });
  }, [onDeleteBuild, deleteConfirm.buildId]);

  const handleEditStart = useCallback((build: BuildData) => {
    setEditingBuild(build.id);
    setEditName(build.name);
  }, []);

  const handleEditSave = useCallback((buildId: string) => {
    if (editName.trim()) {
      onUpdateBuild(buildId, { name: editName.trim() });
    }
    setEditingBuild(null);
    setEditName('');
  }, [editName, onUpdateBuild]);

  const handleEditCancel = useCallback(() => {
    setEditingBuild(null);
    setEditName('');
  }, []);

  const filteredBuilds = useMemo(() => {
    if (!searchTerm) return builds;
    const lowerTerm = searchTerm.toLowerCase();
    return builds.filter(b => 
      b.name.toLowerCase().includes(lowerTerm) || 
      b.knight.toLowerCase().includes(lowerTerm)
    );
  }, [builds, searchTerm]);

  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result;
        if (typeof result === 'string') {
          try {
            const buildData = JSON.parse(result);
            if (isValidBuildData(buildData)) {
              // Merge with defaults to ensure optional fields exist (prevents crashes on old build files)
              const sanitizedBuild = {
                ...buildData,
                nimue: buildData.nimue || { option1: null, option2: null },
                anvil: buildData.anvil || { mode: 'single', upgrades: [] },
                morgana: buildData.morgana || { mode: 'single', modifiers: [] },
                merlin: buildData.merlin || { mode: 'single', upgrades: [] },
                source: buildData.source || {}
              };
              onImportBuild(sanitizedBuild);
            } else {
              alert('Invalid build file format. Missing required fields.');
            }
          } catch (error) {
            alert('Invalid JSON file');
          }
        }
      };
      reader.readAsText(file);
    }
    // Reset input
    event.target.value = '';
  };

  return (
    <div className="space-y-4">
      {/* Import Button */}
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-semibold text-sworn-gold">Saved Builds ({builds.length})</h3>
        <label className="bg-slate-700 hover:bg-slate-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors cursor-pointer flex items-center gap-2">
          <Upload className="w-4 h-4" />
          Import Build
          <input
            type="file"
            accept=".json"
            onChange={handleImport}
            className="hidden"
          />
        </label>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input 
          type="text" 
          placeholder="Search builds..." 
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-slate-800 border border-slate-700 rounded-lg py-2 pl-9 pr-8 text-sm text-white focus:ring-2 focus:ring-sworn-gold focus:border-transparent outline-none"
        />
        {searchTerm && (
          <button 
            onClick={() => setSearchTerm('')} 
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
            aria-label="Clear search"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Builds List */}
      {filteredBuilds.length === 0 ? (
        <div className="text-center py-8 text-slate-400">
          <p>{builds.length === 0 ? "No saved builds yet." : "No builds match your search."}</p>
          {builds.length === 0 && <p className="text-sm mt-1">Create and save your first build!</p>}
        </div>
      ) : (
        <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-2 custom-scrollbar">
          {filteredBuilds.map(build => (
            <SavedBuildItem
              key={build.id}
              build={build}
              editingBuildId={editingBuild}
              editName={editName}
              setEditName={setEditName}
              handleEditSave={handleEditSave}
              handleEditCancel={handleEditCancel}
              handleEditStart={handleEditStart}
              onLoadBuild={onLoadBuild}
              onExportBuild={onExportBuild}
              handleDeleteClick={handleDeleteClick}
              isCurrent={build.id === currentBuildId}
              onDuplicateBuild={onDuplicateBuild}
            />
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, buildId: '', buildName: '' })}
        onConfirm={handleDeleteConfirm}
        title="Delete Build"
        message={`Are you sure you want to delete "${deleteConfirm.buildName}"? This action cannot be undone.`}
      />
    </div>
  );
};

export default SavedBuildsTab;
